import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { AuthCard } from '../../features/auth/components/AuthCard';
import { LoginForm } from '../../features/auth/components/LoginForm';
import { OAuthButtons } from '../../features/auth/components/OAuthButtons';
import { ensureIdentity } from '../../features/auth/useAuth';
import { LandingNav } from '../../features/marketing/LandingNav';
import { PublicLayout } from '../../layouts/PublicLayout';
import { siteRelativePath } from '../../lib/search';

/**
 * Spec 22.3: `UNAUTHENTICATED` redirects here with `preserveReturnTo`. The parameter is
 * validated as a site-relative path — an absolute URL here would be an open redirect,
 * which is exactly how a convincing phishing link gets built out of a login page.
 */
export const Route = createFileRoute('/$lang/login')({
  validateSearch: (search: Record<string, unknown>): { returnTo?: string } => {
    /*
     * The key is OMITTED rather than set to undefined when absent. TanStack derives the
     * search type from this return, and `{ returnTo: string | undefined }` is a REQUIRED
     * property — which would make every `<Link to="/$lang/login">` in the app fail to
     * compile until it passed one.
     */
    const returnTo = siteRelativePath(search['returnTo']);
    return returnTo === undefined ? {} : { returnTo };
  },
  beforeLoad: async ({ context, params, search }) => {
    /* Someone already signed in has no business on the sign-in screen. */
    const identity = await ensureIdentity(context.queryClient);
    if (identity !== null && !identity.isGuest) {
      throw redirect({ to: search.returnTo ?? '/$lang/app', params: { lang: params.lang } });
    }
  },
  component: LoginScreen,
});

function LoginScreen(): React.ReactNode {
  const { lang } = Route.useParams();
  const { returnTo } = Route.useSearch();
  const { t } = useTranslation();

  return (
    <PublicLayout lang={lang} focus headerNav={<LandingNav lang={lang} />}>
      <AuthCard
        eyebrow={t('auth.login.eyebrow')}
        title={t('auth.login.title')}
        subtitle={t('auth.login.subtitle')}
        image={{ face: 'star', caption: `XVII · ${t('auth.card.star')}` }}
        footer={
          <>
            {t('auth.login.noAccount')}{' '}
            <Link to="/$lang/signup" params={{ lang }} className="text-accent underline">
              {t('auth.login.createOne')}
            </Link>
          </>
        }
      >
        <LoginForm lang={lang} {...(returnTo === undefined ? {} : { returnTo })} />

        <OAuthButtons callbackUrl={`${globalThis.location.origin}/${lang}/app`} />
      </AuthCard>
    </PublicLayout>
  );
}
